<template>
  <div class='leaks-tab'>
    <div class='leaks-tab__header'>
      <div class='leaks-tab__header__search'>
        <div class="project-search">
          <filtered-search
            class='search-filter'
            @searchInput="searchChange"
          />
        </div>
      </div>
      <div class='leaks-tab__header__left'>
        <div class='leaks-tab__header__left__type'>
          <span class="leaks-tab__header__left__type__title">{{ $t('leaks.tab.header.type.select.title') }}</span>
          <v-autocomplete
            v-model="typeSelected"
            :items="allTypeOptions.sort()"
            append-icon="expand_more"
            dense
            outlined
            multiple
            height="40"
            :placeholder="$t('leaks.tab.header.type.select.placeholder')"
          >
            <template v-slot:selection="{ index }">
              <div v-if="typeSelected.length === allTypeOptions.length && index === 0">
                <span v-if="typeSelected.length === 1" class="selection-text">{{ typeSelected[0] }}</span>
                <span v-else class="selection-text">{{ $t('leaks.tab.header.type.select.all') }}</span>
              </div>
              <div v-else-if="typeSelected.length !== allTypeOptions.length && index === 0">
                <span v-if="typeSelected.length === 1" class="selection-text">{{ typeSelected[0] }}</span>
                <span v-else class="selection-text">{{ $t('leaks.tab.header.type.select.selected') }} {{ typeSelected.length }} {{ $t('leaks.tab.header.type.select.types') }}</span>
              </div>
            </template>
          </v-autocomplete>
        </div>
        <div class='leaks-tab__header__left__source'>
          <span class="leaks-tab__header__left__source__title">{{ $t('leaks.tab.header.source.select.title') }}</span>
          <v-autocomplete
            v-model="sourceSelected"
            :items="allSourcesOptions.sort()"
            append-icon="expand_more"
            dense
            outlined
            multiple
            height="40"
            :placeholder="$t('leaks.tab.header.source.select.placeholder')"
          >
            <template v-slot:selection="{ index }">
              <div v-if="sourceSelected.length === allSourcesOptions.length && index === 0">
                <span v-if="sourceSelected.length === 1" class="selection-text">{{ sourceSelected[0] }}</span>
                <span v-else class="selection-text">{{ $t('leaks.tab.header.source.select.all') }}</span>
              </div>
              <div v-else-if="sourceSelected.length !== allSourcesOptions.length && index === 0">
                <span v-if="sourceSelected.length === 1" class="selection-text">{{ sourceSelected[0] }}</span>
                <span v-else class="selection-text">{{ $t('leaks.tab.header.source.select.selected') }} {{ sourceSelected.length }} {{ $t('leaks.tab.header.source.select.sources') }}</span>
              </div>
            </template>
          </v-autocomplete>
        </div>
        <div class='leaks-tab__header__left__review'>
          <span class="leaks-tab__header__left__review__title">{{ $t('leaks.tab.header.review.select.title') }}</span>
          <v-autocomplete
            v-model="reviewSelected"
            :items="reviewedOptions"
            append-icon="expand_more"
            dense
            outlined
            multiple
            height="40"
            :placeholder="$t('leaks.tab.header.review.select.placeholder')"
          />
        </div>
        <div class='leaks-tab__header__left__important'>
          <div class="revoke-grant-toggle">
            <v-switch
              v-model="onlyImportantLeaks"
            ></v-switch>
          </div>
          <span class="leaks-tab__header__left__important__title">{{ $t('leaks.tab.header.important.label') }}</span>
        </div>
      </div>
    </div>
    <div class='leaks-tab__content' v-if='!dataLoading && !leaksLoading'>
      <no-data
        v-if='!formattedLeaksFilteredData.length'
        :message='messageForNoData'
      >
        <div slot="action">
          <div class='no-data no-results' v-if='hasFilter && tldsWithLeaksScan.length'>
            <div class='title'>
              {{ noDataMessageTitle }}
            </div>
            <div class='domains'>
              {{ noDataMessageDomains }}
            </div>
            <v-divider v-if='tldsWithNoLeaksScan.length' class='divider'/>
          </div>
          <div class='initiate-test' v-if='hasFilter && tldsWithNoLeaksScan.length'>
            <div class='no-data'>
              <div class='title'>
                {{ noScansMessageTitle }}
              </div>
              <div class='domains'>
                {{ noScansMessageDomains }}
              </div>
            </div>
            <custom-tooltip placement="right" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <v-btn class="initiate-test-btn" @click="initiateLeaksTest" :disabled='initiateTestClicked'>
                  {{ $t('configuration.initiate.test') }}
                </v-btn>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ toolTipText }}
                </slot>
              </div>
            </custom-tooltip>
            <span class="scan-initiated" v-if='testInitiated'>
              {{ scanInitiated }}
            </span>
          </div>
        </div>
      </no-data>
      <div v-else>
        <wombat-table
          :items="formattedLeaksFilteredData"
          :columns="tableColumns"
          :loadingItems="false"
          :resize="false"
          :columnsReorder="false"
          :itemHeight="58"
          @columnSorted="columnSorted"
        >
          <component
            @updateReviewedValue='updateReviewedValue'
            @showLeakContentPopup='showLeakContentPopup'
            @changeMarkedValue='changeMarkedValue'
            slot="row-cell"
            slot-scope="rowCell"
            class="row-cell"
            :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
            :all-items='formattedLeaksFilteredData'
            :item="rowCell.item"
            :column="rowCell.column"
          />
        </wombat-table>
        <div class="leaks-tab__content__footer">
          <advanced-pagination
            tab='leaks'
            :pages-amount='pages'
            :page="page"
            :is-show-pagination='isShowPagination'
            @changedSelectedPage='changedSelectedPage'
            @changedItemsAmountPerPage='changedItemsAmountPerPage'
          />
        </div>
      </div>
    </div>
    <div class='leaks-tab-loading' v-else />
    <page-loading-overlay
      :show="leaksLoading && currentTab === 'leaks'"
      :progress="leaksProgress"
      contained
    />
    <leak-data-popup
      custom-title='leaks.tab.leak.content.popup.title'
      custom-leak-icon='water_drop'
      :visible-popup='displayLeakContentPopup'
      :data='contentForDisplayFormatted'
      @clicked="onClickContentDisplayPopup"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import { getEmptyColumn, leaksDataColumns } from '@/services/tableColumns';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import MarkTableCell from '@/components/tableCells/leaksDataTableCells/MarkTableCell.vue';
import LeakSourceTableCell from '@/components/tableCells/leaksDataTableCells/LeakSourceTableCell.vue';
import LeakContentTableCell from '@/components/tableCells/leaksDataTableCells/LeakContentTableCell.vue';
import LeakReviewedTableCell from '@/components/tableCells/leaksDataTableCells/LeakReviewedTableCell.vue';
import LeakUrlTableCell from '@/components/tableCells/leaksDataTableCells/LeakUrlTableCell.vue';
import MessagePopup from '@/components/popup/MessagePopup.vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import {
  ADD_NEW_TEST_PROJECT, INITIATE_TEST_CARD,
  UPDATE_DATA_LEAKS_CONFIGURATION,
} from '@/store/project/actionTypes';
import {CONFIGURATION, LEAKS_HEADERS} from '@/constants/configuration';
import LeakCommitterTableCell from '@/components/tableCells/leaksDataTableCells/LeakCommitterTableCell.vue';
import LeakKeywordTableCell from '@/components/tableCells/leaksDataTableCells/LeakKeywordTableCell.vue';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import FilteredSearch from '@/components/common/FilteredSearch.vue';
import { TEST_REPEAT_STATUS, TEST_TYPES_TLDS } from '@/constants/projects';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import NoData from '@/components/common/NoData.vue';
import LeakTypeTableCell from '@/components/tableCells/leaksDataTableCells/LeakTypeTableCell.vue';
import {UPDATE_ITEMS_PER_PAGE} from '@/store/pagination/actionTypes';
import AdvancedPagination from '@/components/common/AdvancedPagination.vue';
import {CHANGE_LEAKS_LIST_OPTIONS, UPDATE_DATA_LEAKS_ITEMS} from '@/store/configuration/actionTypes';
import LeakDataPopup from '@/components/popup/LeakDataPopup.vue';

const getTimeWithHourOffset = offset => moment()
  .startOf('hour')
  .add(offset, 'hour')
  .format('HH:mm');

const getTimeWithMinuteOffset = offset => moment()
  .startOf('minute')
  .add(offset, 'minute')
  .format('HH:mm');

export default {
  name: 'LeaksTab',
  components: {
    LeakDataPopup,
    NoData,
    PageLoadingOverlay,
    MessagePopup,
    WombatTable,
    FilteredSearch,
    CustomTooltip,
    AdvancedPagination,
  },
  props: {
    currentTab: {
      required: false,
    },
    hasFilter: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      page: 1,
      sortLeaks: {
        [LEAKS_HEADERS['leaksDataTable.source']]: 'asc',
      },
      search: '',
      typeSelected: [],
      sourceSelected: [],
      reviewSelected: [],
      onlyImportantLeaks: false,
      initiateTestClicked: false,
      testInitiated: false,
      displayLeakContentPopup: false,
      contentForDisplay: Object,
      callCounter: 0,
      firstTimeCalled: false,
      polling: null,
    };
  },
  async beforeDestroy() {
    clearInterval(this.polling);
  },
  methods: {
    async loadLeaks(turnOnLoading = true) {
      if (this.currentTab === 'leaks') {
        clearInterval(this.polling);
        await this.$store.dispatch(CHANGE_LEAKS_LIST_OPTIONS, {
          sort: this.sortLeaks,
          limit: this.itemsPerPage,
          page: this.page,
          search: this.search,
          type: this.typeSelected,
          source: this.sourceSelected,
          review: this.reviewSelected,
          important: this.onlyImportantLeaks,
          domain: this.domainFilter || [],
          organizations: this.organizationFilter || [],
          turnOnLoading,
          tab: 'leaks',
        });
        if (this.leaksRefId && this.leaksRefId !== '') {
          this.polling = setInterval(async () => {
            if (!this.leaksRefId || this.leaksRefId === '') {
              clearInterval(this.polling);
            } else if (!this.leaksRunningInterval) {
              await this.$store.dispatch(CHANGE_LEAKS_LIST_OPTIONS, {
                sort: this.sortLeaks,
                limit: this.itemsPerPage,
                page: this.page,
                search: this.search,
                type: this.typeSelected,
                source: this.sourceSelected,
                review: this.reviewSelected,
                important: this.onlyImportantLeaks,
                domain: this.domainFilter || [],
                organizations: this.organizationFilter || [],
                turnOnLoading,
                tab: 'leaks',
                refId: this.leaksRefId,
              });
            }
          }, 3000);
        }
      }
    },
    searchChange(e) {
      this.search = e.trim();
    },
    extractSearchString() {
      if (!this.search) {
        return '';
      }
      let results = this.search.split(' ');
      results = results.filter(result => !result.includes(':'));
      return results.join(' ').trim();
    },
    async initiateLeaksTest() {
      this.initiateTestClicked = true;
      const selectedTimeZone = this.formattedTimeZone.find(timezone => timezone.value === String(moment().utcOffset()));
      for (let i = 0; i < this.tldsWithNoLeaksScan.length; i += 1) {
        try {
          const test = {
            url: this.tldsWithNoLeaksScan[i],
            tld: this.tldsWithNoLeaksScan[i],
            type: TEST_TYPES_TLDS.dataleaks,
            schedule: false,
            isAllDay: false,
            startDate: moment().format(),
            startTime: getTimeWithMinuteOffset(2),
            endTime: getTimeWithHourOffset(2),
            timezone: selectedTimeZone.id,
            customRepeatData: {},
            frequency: TEST_REPEAT_STATUS.never,
          };
          // eslint-disable-next-line no-await-in-loop
          const createdTest = await this.$store.dispatch(ADD_NEW_TEST_PROJECT, test);
          if (createdTest) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await this.$store.dispatch(INITIATE_TEST_CARD, [createdTest.id]);
            } catch (e) {
              this.errorMessage = e.message;
            }
          }
        } catch (e) {
          this.errorMessage = e.message;
        }
      }
      this.testInitiated = true;
    },
    showLeakContentPopup(leakDataItem) {
      this.contentForDisplay = leakDataItem.item.content;
      this.displayLeakContentPopup = true;
    },
    async changeMarkedValue(leakDataItem) {
      const newLeakItem = this.findLeak(leakDataItem) || null;
      await this.$store.dispatch(UPDATE_DATA_LEAKS_CONFIGURATION, {
        data: [{
          metaData: [{
            important: !newLeakItem?.mark || false,
            reviewed: newLeakItem?.reviewed || false,
            content: leakDataItem.item.content,
          }],
          data: leakDataItem.item.leakUrl,
          tld: leakDataItem.item.keyword,
        }],
        configType: CONFIGURATION.CONFIG_TYPES.DATALEAKS,
      });
      // eslint-disable-next-line max-len
      const items = this.leaksItems.map(item => (item.keyword === leakDataItem.item.keyword && item.leakUrl === leakDataItem.item.leakUrl && item.content === leakDataItem.item.content ? {...item, mark: !item.mark} : item));
      await this.$store.dispatch(UPDATE_DATA_LEAKS_ITEMS, items);
    },
    async updateReviewedValue(leakDataItem) {
      const newLeakItem = this.findLeak(leakDataItem) || null;
      await this.$store.dispatch(UPDATE_DATA_LEAKS_CONFIGURATION, {
        data: [{
          metaData: [{
            important: newLeakItem?.mark || false,
            reviewed: !newLeakItem?.reviewed || false,
            content: leakDataItem.item.content,
          }],
          data: leakDataItem.item.leakUrl,
          tld: leakDataItem.item.keyword,
        }],
        configType: CONFIGURATION.CONFIG_TYPES.DATALEAKS,
      });
      // eslint-disable-next-line max-len
      const items = this.leaksItems.map(item => (item.keyword === leakDataItem.item.keyword && item.leakUrl === leakDataItem.item.leakUrl && item.content === leakDataItem.item.content ? {...item, reviewed: !item.reviewed} : item));
      await this.$store.dispatch(UPDATE_DATA_LEAKS_ITEMS, items);
    },
    onClickContentDisplayPopup() {
      this.displayLeakContentPopup = false;
    },
    findLeak(leakDataItem) {
      let newLeakItem = null;
      this.leaksItems.forEach(leak => {
        if (leak.content === leakDataItem.item.content && leak.keyword === leakDataItem.item.keyword && leak.leakUrl === leakDataItem.item.leakUrl) {
          newLeakItem = leak;
        }
      });
      return newLeakItem;
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'leaks'});
      await this.loadLeaks();
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.loadLeaks();
    },
    async columnSorted({ column, direction }) {
      this.sortLeaks = {
        [column.sortingField || column.field]: direction,
      };
    },
  },
  computed: {
    ...mapGetters([
      'dataLoading',
      'leaksLoading',
      'domainFilter',
      'organizationFilter',
      'leaksPaginationState',
      'paginationItemsPerPageByTabs',
      'leaksItems',
      'tldsWithNoLeaksScan',
      'tldsWithLeaksScan',
      'leaksSourceOptions',
      'leaksTypeOptions',
      'leaksListSort',
      'leaksRunningInterval',
      'leaksRefId',
      'leaksProgress',
    ]),
    contentForDisplayFormatted() {
      if (Array.isArray(this.contentForDisplay)) {
        return this.contentForDisplay;
      }
      return [this.contentForDisplay];
    },
    pages() {
      return this.leaksPaginationState?.pageCount || Math.ceil(this.leaksItems.length / this.itemsPerPage);
    },
    isShowPagination() {
      return this.pages > 1;
    },
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs.leaks;
    },
    messageForNoData() {
      return !this.hasFilter
        ? this.$t('no.filter.infrastructure.no.data.message')
        : '';
    },
    toolTipText() {
      return this.$t('configuration.add.test');
    },
    scanInitiated() {
      return this.$t('test.initiated.successfully');
    },
    formattedTimeZone() {
      return getGroupedTimezonesList().map(tz => ({
        value: tz.offset,
        name: tz.name,
        id: tz.id,
      }));
    },
    allTldsHasLeaksScan() {
      return !this.tldsWithNoLeaksScan.length;
    },
    noDataMessage() {
      return this.allTldsHasLeaksScan ? this.$t('no.results.found') : '';
    },
    noScansMessageTitle() {
      return this.$t('no.leaks.scans.found.on');
    },
    noDataMessageTitle() {
      return this.$t('no.results.found');
    },
    noScansMessageDomains() {
      return `${this.tldsWithNoLeaksScan?.length ? this.tldsWithNoLeaksScan.join(', ') : ''}`;
    },
    noDataMessageDomains() {
      return `${this.tldsWithNoLeaksScan?.length ? this.tldsWithLeaksScan.join(', ') : ''}`;
    },
    formattedLeaksFilteredData() {
      const finalItems = [];
      for (let i = 0; i < this.leaksItems.length; i += 1) {
        finalItems.push({ id: i + 1, ...this.leaksItems[i] });
      }
      return finalItems;
    },
    allSourcesOptions() {
      return [...this.leaksSourceOptions];
    },
    allTypeOptions() {
      return [...this.leaksTypeOptions];
    },
    reviewedOptions() {
      return ['True', 'False'];
    },
    tableRowCellComponentsHash() {
      return {
        mark: MarkTableCell,
        content: LeakContentTableCell,
        source: LeakSourceTableCell,
        reviewed: LeakReviewedTableCell,
        committer: LeakCommitterTableCell,
        keyword: LeakKeywordTableCell,
        leakType: LeakTypeTableCell,
        leakUrl: LeakUrlTableCell,
        default: WombatRowDefaultCell,
      };
    },
    tableColumns() {
      const columns = [
        leaksDataColumns.mark,
        leaksDataColumns.leakType,
        leaksDataColumns.content,
        leaksDataColumns.source,
        leaksDataColumns.committer,
        leaksDataColumns.leakUrl,
        leaksDataColumns.reviewed,
        leaksDataColumns.keyword,
      ].map(column => ({
        ...column,
        title: column.title && this.$t(column.title),
        sort: this.leaksListSort[column.sortingField || column.field],
      }));

      return [getEmptyColumn(32), ...columns, getEmptyColumn(85)];
    },
  },
  watch: {
    async currentTab() {
      if (this.currentTab === 'leaks' && this.domainFilter.length && !this.firstTimeCalled) {
        this.firstTimeCalled = true;
        await this.loadLeaks();
      }
    },
    async domainFilter() {
      if (this.currentTab !== 'leaks' && this.domainFilter.length) {
        this.firstTimeCalled = false;
      }
      if (this.currentTab === 'leaks' && this.domainFilter.length) {
        this.callCounter = 1;
        this.page = 1;
        this.search = '';
        this.typeSelected = [];
        this.sourceSelected = [];
        this.reviewSelected = [];
        await this.loadLeaks();
        this.callCounter = 0;
      }
    },
    async sortLeaks() {
      this.page = 1;
      await this.loadLeaks();
    },
    async search() {
      if (this.callCounter === 0) {
        this.page = 1;
        await this.loadLeaks();
      }
    },
    async typeSelected() {
      if (this.callCounter === 0) {
        this.page = 1;
        await this.loadLeaks();
      }
    },
    async sourceSelected() {
      if (this.callCounter === 0) {
        this.page = 1;
        await this.loadLeaks();
      }
    },
    async reviewSelected() {
      if (this.callCounter === 0) {
        this.page = 1;
        await this.loadLeaks();
      }
    },
    async onlyImportantLeaks() {
      this.page = 1;
      await this.loadLeaks();
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.leaks-tab {
  display: flex;
  flex-direction: column;
  position: relative; // Required for contained loading overlay
  min-height: calc(100vh - 200px); // Ensure overlay has space to display

  .initiate-test-btn.initiate-test-btn {
    width: fit-content;
    padding: $space-3 $space-6;
    background-color: transparent !important;
    border: 1px solid var(--border-primary) !important;
    border-radius: $radius-sm;
    box-shadow: $shadow-sm;
    color: var(--text-primary) !important;
    font-family: $default-application-font-medium;
    font-size: $font-size-sm;
    font-weight: 500;
    letter-spacing: 1.1px;
    line-height: $font-size-md;
    text-align: center;
    margin-bottom: $space-2;
    margin-top: $space-1;
    text-transform: uppercase;
    &:hover {
      background-color: var(--bg-tertiary);
      border-color: var(--border-secondary);
    }
  }

  &__header {
    display: flex;
    align-items: center;

    &__left {
      display: flex;

      &__type {
        width: rem(200);
        margin-left: $space-3;

        :deep(.v-input) {
          margin-top: 0;
        }

        &__title {
          font-family: $default-application-font-regular;
          font-size: $font-size-xs;
          letter-spacing: 0.8px;
          line-height: $font-size-xs;
          margin-bottom: $space-1;
          color: var(--text-secondary);
        }
      }

      &__source {
        width: rem(200);
        margin-left: $space-3;

        :deep(.v-input) {
          margin-top: 0;
        }

        &__title {
          font-family: $default-application-font-regular;
          font-size: $font-size-xs;
          letter-spacing: 0.8px;
          line-height: $font-size-xs;
          margin-bottom: $space-1;
          color: var(--text-secondary);
        }
      }

      &__review {
        width: rem(200);
        margin-left: $space-3;

        :deep(.v-input) {
          margin-top: 0;
        }

        &__title {
          font-family: $default-application-font-regular;
          font-size: $font-size-xs;
          letter-spacing: 0.8px;
          line-height: $font-size-xs;
          margin-bottom: $space-1;
          color: var(--text-secondary);
        }
      }

      &__important {
        width: rem(270);
        margin-left: $space-10;
        display: flex;
        align-items: center;
        margin-top: $space-1;

        &__title {
          flex: 80%;
          color: var(--text-secondary);
          font-family: $default-application-font-regular;
          font-size: $font-size-md;
        }

        .revoke-grant-toggle {
          display: flex;
        }
      }
    }

    &__search {
      margin-top: $space-0-5;
      margin-right: $space-10;

      .project-search {
        width: rem(500);
      }
    }
  }

  &__content {
    margin-top: $space-5;
  }
}

.leaks-tab-loading {
  align-self: center;
  justify-self: center;
  margin-top: 20%;
}

.no-data {
  &.no-results {
    align-items: center;
    margin-bottom: $space-2;

    .domains {
      margin-bottom: $space-1;
      font-size: $font-size-base;
      text-align: left;
    }

    .divider {
      width: 100%;
    }
  }
}

.initiate-test {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .no-data {
    .domains {
      margin-bottom: $space-1;
      font-size: $font-size-base;
      text-align: left;
    }
  }
}

.no-result-found {
  .no-scans {
    .initiate-test {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-items: center;

      .scan-initiated {
        color: var(--brand-primary);
        font-family: $default-application-font-semi-bold;
        font-size: $font-size-sm;
        text-transform: uppercase;
      }
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.leaks-tab {
  .no-result-found {
    max-width: 40%;
    text-align: center;
    font-size: $font-size-lg;
    margin-top: 7%;
    display: flex;
    flex-direction: column;
    justify-items: center;
    color: var(--text-muted);
    opacity: 1;

    .no-scans {
      margin-bottom: $space-8;

      .title {
        display: flex;
        justify-content: center;
        font-size: $font-size-4xl;
        margin: auto;
        overflow: hidden;
        text-transform: capitalize;
        opacity: 0.6;
      }

      .domains {
        text-align: left;
        margin-bottom: $space-4;
        opacity: 0.6;
      }
    }

    .no-data {
      margin-bottom: $space-5;

      .title {
        display: flex;
        justify-content: center;
        font-size: $font-size-4xl;
        margin: auto;
        overflow: hidden;
        text-transform: capitalize;
        opacity: 0.6;
      }

      .domains {
        font-size: $font-size-base;
        text-align: left;
        opacity: 0.6;
      }
    }
  }

  .v-input__icon.v-input__icon--append button {
    font-size: $font-size-lg;
    margin-top: 0;
  }

  padding: 0 $space-8 $space-4 $space-8;
  margin-top: rem(-2);

  .wombat-table {
    --displayactioncell: none;
    padding: $space-4 0;

    .row-cell {
      display: flex;
      align-items: center;
    }

    .wombat-row {
      border: none;
    }

    .wombat-overlay {
      border-bottom: none;

      &:hover {
        background-color: var(--bg-tertiary);
        --display: block;
        --bg: var(--bg-tertiary);
        --displayactioncell: flex;
      }
    }
  }
}

.leaks-tab__content {
  .wombat-table {
    .wombat-row {
      border-top: none;
      border-left: none;
      border-right: none;
      border-bottom: 1px solid var(--border-primary);
    }
  }

}

.leaks-tab__header__left {
  .v-input__append-inner {
    align-self: center !important;
    margin-top: 0 !important;
  }

  .v-input__icon--append {
    align-self: center;
    margin-top: 0;

    .v-icon {
      color: var(--text-muted);
    }
  }

  &.background {
    min-width: rem(300);
    max-width: rem(300);
    margin-top: $space-1;

    .v-select__slot {
      background-color: transparent;
    }
  }

  .v-select__selections {
    margin-left: $space-1;
    height: $space-10;

    .selection-text {
      margin-left: $space-2;
    }
  }
}
</style>
